import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { HEARD_FROM, INTERESTS, GENDERS } from "@/lib/constants";
import { toast } from "sonner";

const blank = { full_name: "", mobile: "", age: "", gender: "", area: "", pincode: "", first_time: "", heard_from: "", interests: [], wants_contact: "Yes", preferred_contact: "WhatsApp", assigned_to: "", notes: "" };

const Pick = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map((o) => {
      const on = Array.isArray(value) ? value.includes(o) : value === o;
      return (
        <button key={o} type="button" onClick={() => onChange(o)} data-testid={`${testid}-${o.replace(/[^a-z]/gi, "").toLowerCase()}`}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-[#081226] text-white border-[#E8752A]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>{o}</button>
      );
    })}
  </div>
);

const L = ({ children, req }) => <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">{children}{req && <span className="text-[#D95D1E]"> *</span>}</label>;

export const AddLeadDialog = ({ open, onClose, team, onSaved }) => {
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(blank); }, [open]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggle = (i) => set("interests", f.interests.includes(i) ? f.interests.filter((x) => x !== i) : [...f.interests, i]);
  const valid = f.full_name.trim() && /^\d{10}$/.test(f.mobile);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.post("/leads/manual", { ...f, age: f.age ? parseInt(f.age) : null, assigned_to: f.assigned_to || null, gender: f.gender || null, first_time: f.first_time || null, heard_from: f.heard_from || null });
      toast.success(`Lead ${res.data.lead_id} added · ${res.data.visitor_id}`); onClose(); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add lead"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-lead-dialog">
        <DialogHeader><DialogTitle>Add Lead</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><L req>Full Name</L><Input value={f.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Client name" data-testid="add-lead-name-input" /></div>
            <div><L req>Mobile Number</L><Input value={f.mobile} inputMode="numeric" maxLength={10} onChange={(e) => set("mobile", e.target.value.replace(/\D/g, ""))} placeholder="10-digit mobile" data-testid="add-lead-mobile-input" /></div>
            <div><L>Age</L><Input value={f.age} inputMode="numeric" onChange={(e) => set("age", e.target.value.replace(/\D/g, ""))} placeholder="Age" data-testid="add-lead-age-input" /></div>
            <div><L>Gender</L><div className="mt-1"><Pick options={GENDERS} value={f.gender} onChange={(v) => set("gender", v)} testid="add-lead-gender" /></div></div>
            <div><L>Area / Address</L><Input value={f.area} onChange={(e) => set("area", e.target.value)} placeholder="e.g. Mota Varachha" data-testid="add-lead-area-input" /></div>
            <div><L>Pin Code</L><Input value={f.pincode} inputMode="numeric" maxLength={6} onChange={(e) => set("pincode", e.target.value.replace(/\D/g, ""))} placeholder="e.g. 394101" data-testid="add-lead-pincode-input" /></div>
          </div>
          <div><L>First time visiting?</L><div className="mt-1"><Pick options={["Yes", "No"]} value={f.first_time} onChange={(v) => set("first_time", v)} testid="add-lead-firsttime" /></div></div>
          <div><L>How did they hear about us?</L><div className="mt-1"><Pick options={HEARD_FROM} value={f.heard_from} onChange={(v) => set("heard_from", v)} testid="add-lead-heard" /></div></div>
          <div><L>Interested in</L><div className="mt-1"><Pick options={INTERESTS} value={f.interests} onChange={toggle} testid="add-lead-interest" /></div></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><L>Wants to be contacted?</L><div className="mt-1"><Pick options={["Yes", "No"]} value={f.wants_contact} onChange={(v) => set("wants_contact", v)} testid="add-lead-contact" /></div></div>
            {f.wants_contact === "Yes" && <div><L>Preferred contact</L><div className="mt-1"><Pick options={["WhatsApp", "Phone Call"]} value={f.preferred_contact} onChange={(v) => set("preferred_contact", v)} testid="add-lead-pref" /></div></div>}
          </div>
          <div><L>Assign to</L>
            <Select value={f.assigned_to} onValueChange={(v) => set("assigned_to", v)}>
              <SelectTrigger className="mt-1" data-testid="add-lead-assign-select"><SelectValue placeholder="Select team member (optional)" /></SelectTrigger>
              <SelectContent>{team.map((t) => <SelectItem key={t.member_id} value={t.member_id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><L>Notes</L><Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} placeholder="Anything the team should know" className="mt-1" data-testid="add-lead-notes-textarea" /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={!valid || saving} className="btn-orange" data-testid="add-lead-save-btn">Add Lead</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
